import { Dispatch, SetStateAction, useCallback } from 'react';

import { readFileAsBase64 } from '@verantos-dev/shared-utils';
import { TargetType } from '@verantos-dev/types-common';

import { ConnectionStatus, GatewayMessage } from '../../types/gateway-chat-types';

interface ChatFilePayload {
  name: string;
  data: string;
  type: string;
  size: number;
}

export interface ChatMessageSenderArgs {
  prompt: string;
  displayContent: string;
  file?: File | null;
}

interface BuildRequestPayloadArgs {
  prompt: string;
  displayContent: string;
  encodedFile?: ChatFilePayload;
  selectedAgent?: string; // Add selected agent parameter
}

export interface UseChatMessageSenderOptions {
  authenticatedFetch: (url: string, options?: RequestInit) => Promise<Response>;
  buildExecuteUrl: (conversationId: string) => string;
  getConversationId: () => string;
  getConnectionStatus: () => ConnectionStatus;
  setMessages: Dispatch<SetStateAction<GatewayMessage[]>>;
  encodeFile?: (file: File) => Promise<ChatFilePayload>;
  buildRequestPayload?: (args: BuildRequestPayloadArgs) => Record<string, unknown>;
  buildUserMessage?: (args: ChatMessageSenderArgs) => GatewayMessage;
}

const defaultEncodeFile = async (file: File): Promise<ChatFilePayload> => ({
  name: file.name,
  data: await readFileAsBase64(file),
  type: file.type || 'csv',
  size: file.size,
});

const defaultBuildRequestPayload = ({
  prompt,
  encodedFile,
  selectedAgent = 'omop_mapping', // Default to omop_mapping agent
}: BuildRequestPayloadArgs): Record<string, unknown> => ({
  target: {
    type: TargetType.AGENT,
    provider: selectedAgent, // Use selected agent
  },
  payload: {
    prompt,
    ...(encodedFile ? { file_data: encodedFile } : {}),
  },
});

const defaultBuildUserMessage = ({
  prompt,
  displayContent,
}: ChatMessageSenderArgs): GatewayMessage => ({
  id: `user-${Date.now()}`,
  role: 'user',
  content: displayContent ?? prompt,
  status: 'sending',
  timestamp: new Date().toISOString(),
});

export const useChatMessageSender = ({
  authenticatedFetch,
  buildExecuteUrl,
  getConversationId,
  getConnectionStatus,
  setMessages,
  encodeFile = defaultEncodeFile,
  buildRequestPayload = defaultBuildRequestPayload,
  buildUserMessage = defaultBuildUserMessage,
}: UseChatMessageSenderOptions) => {
  const sendMessage = useCallback(
    async ({ prompt, displayContent, file }: ChatMessageSenderArgs) => {
      const conversationId = getConversationId();
      if (!conversationId) {
        throw new Error('No active conversation');
      }

      if (getConnectionStatus() !== 'connected') {
        throw new Error('Connection is not ready');
      }

      const userMessage = buildUserMessage({ prompt, displayContent, file });
      setMessages((prev) => [...prev, userMessage]);

      const updateMessage = (updates: Partial<GatewayMessage>) => {
        setMessages((prev) =>
          prev.map((msg) => (msg.id === userMessage.id ? { ...msg, ...updates } : msg))
        );
      };

      try {
        let encodedFile: ChatFilePayload | undefined;
        if (file) {
          encodedFile = await encodeFile(file);
        }

        const response = await authenticatedFetch(buildExecuteUrl(conversationId), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(buildRequestPayload({ prompt, displayContent, encodedFile })),
        });

        if (!response.ok) {
          throw new Error(`Execute failed: ${response.statusText}`);
        }

        await response.json();

        updateMessage({ status: 'complete' });
      } catch (error) {
        console.error('Failed to execute:', error);
        updateMessage({ status: 'error', content: String(error) });
        throw error;
      }
    },
    [
      authenticatedFetch,
      buildExecuteUrl,
      buildRequestPayload,
      buildUserMessage,
      encodeFile,
      getConnectionStatus,
      getConversationId,
      setMessages,
    ]
  );

  return {
    sendMessage,
  };
};
